import { useParams, Link, Navigate } from "react-router-dom";
import { SiteShell } from "@/components/site/SiteShell";
import { PageHero } from "@/components/site/PageHero";
import { SEO, breadcrumbSchema, faqSchema, serviceSchema } from "@/components/site/SEO";
import { FAQ } from "@/components/site/FAQ";
import { PortfolioCategoryStrip } from "@/components/site/PortfolioCategoryStrip";
import { ReviewsSection } from "@/components/site/ReviewsSection";
import { Button } from "@/components/ui/button";
import { Check, ArrowRight, Calendar } from "lucide-react";
import { getServiceBySlug } from "@/lib/servicesData";
import { CALENDLY_URL } from "@/lib/siteData";
import { getPortfolioCategoriesFor } from "@/lib/portfolioData";
import { ROUTE_SEO } from "@/lib/routeSeo";

export default function ServiceDetail() {
  const { slug } = useParams();
  const service = getServiceBySlug(slug);
  if (!service) return <Navigate to="/services" replace />;

  const routePath = `/services/${service.slug}`;
  const seoEntry = ROUTE_SEO[routePath];
  const seoTitle = seoEntry ? seoEntry.title : service.metaTitle;
  const seoMeta = seoEntry ? seoEntry.meta : service.metaDescription;
  const heroH1 = seoEntry ? seoEntry.h1 : service.h1;
  const portfolioCats = getPortfolioCategoriesFor(routePath);

  return (
    <SiteShell>
      <SEO
        title={seoTitle}
        description={seoMeta}
        path={routePath}
        jsonLd={[
          breadcrumbSchema([{name: "Home", path: "/"}, {name: "Services", path: "/services"}, {name: heroH1}]),
          faqSchema(service.faqs || []),
          serviceSchema({ name: heroH1, description: seoMeta, slug: service.slug, image: service.heroImage }),
        ]}
      />
      <PageHero
        kicker={service.kicker}
        title={heroH1}
        subtitle={service.headline}
        breadcrumb={[{name: "Home", path: "/"}, {name: "Services", path: "/services"}, {name: heroH1}]}
        rightSlot={
          <div className="flex flex-col gap-3">
            <Button asChild data-testid="service-calendly-cta" className="h-12 rounded-full bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] hover:brightness-110 px-6"><a href={CALENDLY_URL} target="_blank" rel="noreferrer noopener"><Calendar className="mr-2 h-4 w-4" /> Book a 15-min call</a></Button>
            <Button asChild variant="ghost" className="h-12 rounded-full border border-border bg-card/60 hover:bg-card px-5"><Link to="/contact">Send a brief <ArrowRight className="ml-2 h-4 w-4" /></Link></Button>
            <Button asChild variant="ghost" className="h-12 rounded-full border border-border bg-card/60 hover:bg-card px-5"><Link to="/portfolio">See our work</Link></Button>
          </div>
        }
      />

      <section className="py-12 sm:py-16">
        <div className="container-site grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-7 space-y-12">
            <p className="text-base sm:text-lg text-muted-foreground leading-relaxed">{service.intro}</p>
            {(service.sections || []).map((sec) => (
              <div key={sec.title}>
                <h2 className="font-display uppercase text-foreground text-2xl sm:text-3xl tracking-[0.02em]">{sec.title}</h2>
                <ul className="mt-5 space-y-3">
                  {(sec.bullets || []).map((b) => (
                    <li key={b} className="flex items-start gap-3">
                      <span className="inline-flex h-6 w-6 mt-0.5 items-center justify-center rounded-full bg-[hsl(var(--primary)/0.16)] text-[hsl(var(--primary))] shrink-0"><Check className="h-3.5 w-3.5" /></span>
                      <span className="text-sm sm:text-base text-foreground/90 leading-relaxed">{b}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
          <aside className="lg:col-span-5">
            <div className="rounded-2xl border border-border bg-card p-6 sm:p-7 sticky top-24">
              <p className="kicker mb-3">Get a quote</p>
              <p className="font-display text-2xl uppercase tracking-[0.02em] text-foreground leading-tight">Send a brief. Get a line-item quote within 1 business day.</p>
              <p className="mt-3 text-sm text-muted-foreground leading-relaxed">Tell us your objective, deliverable, dates and budget range. We’ll come back with a treatment direction and clear estimate - free.</p>
              <Button asChild className="mt-6 w-full h-11 rounded-full bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] hover:brightness-110"><Link to="/contact">Start a project</Link></Button>
              <Button asChild variant="ghost" className="mt-2 w-full h-11 rounded-full border border-border bg-card hover:bg-[hsl(225,18%,9%)]"><a href={CALENDLY_URL} target="_blank" rel="noreferrer noopener"><Calendar className="mr-2 h-4 w-4" />Book a 15-min call</a></Button>
              <ul className="mt-6 space-y-2 text-xs text-muted-foreground">
                <li className="flex items-center gap-2"><Check className="h-3.5 w-3.5 text-[hsl(var(--primary))]" /> Senior crew, cinema-grade kit</li>
                <li className="flex items-center gap-2"><Check className="h-3.5 w-3.5 text-[hsl(var(--primary))]" /> In-house post under one roof</li>
                <li className="flex items-center gap-2"><Check className="h-3.5 w-3.5 text-[hsl(var(--primary))]" /> Full rights transfer on delivery</li>
              </ul>
            </div>
          </aside>
        </div>
      </section>

      {portfolioCats.length > 0 && (
        <PortfolioCategoryStrip
          categories={portfolioCats}
          heading={`Recent ${heroH1.toLowerCase()} work.`}
          subheading={`Selected pieces from the ${portfolioCats.map((s) => s.replace("-", " ")).join(" and ")} portfolio.`}
        />
      )}

      <section className="section-pad">
        <div className="container-site">
          <p className="kicker mb-3">FAQ</p>
          <h2 className="font-display uppercase text-foreground text-3xl sm:text-4xl mb-10">Common questions.</h2>
          <FAQ faqs={service.faqs || []} />
        </div>
      </section>

      <ReviewsSection
        kicker="Client reviews"
        title="Don't take our word for it."
        subtitle="Live Google reviews from clients we've delivered work for. Every review is public and verified."
      />

      <section className="section-pad bg-[hsl(225,22%,4%)] border-t border-border">
        <div className="container-site text-center">
          <h2 className="font-display uppercase text-foreground text-3xl sm:text-5xl text-balance">Ready to brief your next project?</h2>
          <p className="mt-4 max-w-xl mx-auto text-sm sm:text-base text-muted-foreground">A short paragraph is enough. We’ll come back with a creative response, treatment, and a transparent estimate.</p>
          <div className="mt-8 inline-flex flex-wrap gap-3">
            <Button asChild className="h-12 rounded-full bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] hover:brightness-110 px-6"><Link to="/contact">Send a brief <ArrowRight className="ml-2 h-4 w-4" /></Link></Button>
            <Button asChild variant="ghost" className="h-12 rounded-full border border-border bg-card/60 hover:bg-card px-5"><a href={CALENDLY_URL} target="_blank" rel="noreferrer noopener"><Calendar className="mr-2 h-4 w-4" />Book a call</a></Button>
            <Button asChild variant="ghost" className="h-12 rounded-full border border-border bg-card/60 hover:bg-card px-5"><Link to="/services">All services</Link></Button>
          </div>
        </div>
      </section>
    </SiteShell>
  );
}
